import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Progress } from '../ui/progress';
import { AlertCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  MessageSquare,
  Calendar as CalendarIcon,
  Clock,
  Target,
  TrendingUp,
  TrendingDown,
  Users,
  CheckCircle2,
  AlertTriangle,
  Briefcase,
  DollarSign,
  ArrowRight,
  ChevronRight,
  Eye,
  Zap,
  Lightbulb,
  Star
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell
} from 'recharts';
import {
  getDashboardSummary,
  getPerformanceByTheme,
  getFeedbackTrends,
  getTeamInsights,
  getMyRecentFeedbacks,
  getMyOneOnOnes,
  getClimateTrends,
  getMyIndividualProgress,
  getTeamPerformance,
  getComparativeAnalytics,
  getOKRProgress
} from '@/api/performance';
import {
  DashboardSummary,
  PerformanceByTheme,
  FeedbackTrends,
  TeamInsights,
  Feedback,
  OneOnOneMeeting,
  ClimateTrends as ClimateTrendsType,
  IndividualProgressResponse,
  TeamPerformanceResponse,
  ComparativeAnalytics,
  OKRProgressAnalytics
} from '@/types/performance';
import { useTenant } from '@/hooks/useTenant';
import { getMyAllReports } from '@/api/workforce';
import type { Employee } from '@/types/workforce';

// Toggle to show/hide non-feedback sections
const SHOW_ALL_SECTIONS = false;

const ResumoGeral = () => {
  const { tenantDomain } = useTenant();

  // State for main dashboard data
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [performanceByTheme, setPerformanceByTheme] = useState<PerformanceByTheme[]>([]);
  const [feedbackTrends, setFeedbackTrends] = useState<FeedbackTrends | null>(null);
  const [teamInsights, setTeamInsights] = useState<TeamInsights | null>(null);

  // State for REAL employee data from workforce API
  const [realEmployees, setRealEmployees] = useState<Employee[]>([]);

  // State for additional data sections
  const [recentFeedbacks, setRecentFeedbacks] = useState<Feedback[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<OneOnOneMeeting[]>([]);
  const [climateTrends, setClimateTrends] = useState<ClimateTrendsType | null>(null);
  const [individualProgress, setIndividualProgress] = useState<IndividualProgressResponse | null>(null);
  const [teamPerformance, setTeamPerformance] = useState<TeamPerformanceResponse | null>(null);
  const [comparativeAnalytics, setComparativeAnalytics] = useState<ComparativeAnalytics | null>(null);
  const [okrProgress, setOKRProgress] = useState<OKRProgressAnalytics | null>(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [feedbacksLoading, setFeedbacksLoading] = useState(true);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  const [climateLoading, setClimateLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(true);
  const [teamPerfLoading, setTeamPerfLoading] = useState(true);
  const [comparativeLoading, setComparativeLoading] = useState(true);
  const [okrProgressLoading, setOKRProgressLoading] = useState(true);

  const [error, setError] = useState<Error | null>(null);

  // Feedback detail modal state
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const handleFeedbackClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setIsFeedbackModalOpen(true);
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const data = await getDashboardSummary(tenantDomain);
        setDashboardData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard summary:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    if (tenantDomain) {
      fetchDashboardData();
    }
  }, [tenantDomain]);

  // Fetch analytics data on component mount
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setAnalyticsLoading(true);

        // Fetch performance by theme
        const performanceData = await getPerformanceByTheme(tenantDomain);
        setPerformanceByTheme(performanceData);

        // Fetch feedback trends
        const trendsData = await getFeedbackTrends(tenantDomain);
        setFeedbackTrends(trendsData);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        // Don't set error state - allow dashboard to render with empty analytics
      } finally {
        setAnalyticsLoading(false);
      }
    };

    if (tenantDomain) {
      fetchAnalyticsData();
    }
  }, [tenantDomain]);

  // Fetch team insights for managers
  useEffect(() => {
    const fetchTeamInsights = async () => {
      try {
        setInsightsLoading(true);
        const insights = await getTeamInsights(tenantDomain, { period: 90 });
        setTeamInsights(insights || null);
      } catch (err: any) {
        // Silently handle errors - user might not have manager permissions or profile not loaded
        // This is expected for non-manager users
        setTeamInsights(null);
      } finally {
        setInsightsLoading(false);
      }
    };

    if (tenantDomain) {
      fetchTeamInsights();
    }
  }, [tenantDomain]);

  // Fetch recent feedbacks
  useEffect(() => {
    const fetchRecentFeedbacks = async () => {
      try {
        setFeedbacksLoading(true);
        const feedbacks = await getMyRecentFeedbacks(tenantDomain);
        // Ensure feedbacks is always an array
        setRecentFeedbacks(Array.isArray(feedbacks) ? feedbacks : []);
      } catch (err) {
        console.error('Error fetching recent feedbacks:', err);
        // Don't set error - allow dashboard to render with empty feedbacks
        setRecentFeedbacks([]);
      } finally {
        setFeedbacksLoading(false);
      }
    };

    if (tenantDomain) {
      fetchRecentFeedbacks();
    }
  }, [tenantDomain]);

  // Fetch upcoming meetings
  useEffect(() => {
    const fetchUpcomingMeetings = async () => {
      try {
        setMeetingsLoading(true);
        const response = await getMyOneOnOnes(tenantDomain, {
          upcoming: true,
          limit: 3,
          status: 'confirmed'
        });
        // Ensure we always set an array
        setUpcomingMeetings(Array.isArray(response?.items) ? response.items : []);
      } catch (err) {
        console.error('Error fetching upcoming meetings:', err);
        // Don't set error - allow dashboard to render with empty meetings
        setUpcomingMeetings([]);
      } finally {
        setMeetingsLoading(false);
      }
    };

    if (tenantDomain) {
      fetchUpcomingMeetings();
    }
  }, [tenantDomain]);

  // Fetch climate trends
  useEffect(() => {
    const fetchClimateTrends = async () => {
      try {
        setClimateLoading(true);
        const trends = await getClimateTrends(tenantDomain, { period: 12 });
        setClimateTrends(trends || null);
      } catch (err) {
        console.error('Error fetching climate trends:', err);
        // Don't set error - allow dashboard to render without climate data
        setClimateTrends(null);
      } finally {
        setClimateLoading(false);
      }
    };

    if (tenantDomain) {
      fetchClimateTrends();
    }
  }, [tenantDomain]);

  // Fetch individual progress
  useEffect(() => {
    const fetchIndividualProgress = async () => {
      try {
        setProgressLoading(true);
        const progress = await getMyIndividualProgress(tenantDomain);
        setIndividualProgress(progress || null);
      } catch (err) {
        console.error('Error fetching individual progress:', err);
        // Don't set error - allow dashboard to render without progress data
        setIndividualProgress(null);
      } finally {
        setProgressLoading(false);
      }
    };

    if (tenantDomain) {
      fetchIndividualProgress();
    }
  }, [tenantDomain]);

  // Fetch team performance
  useEffect(() => {
    const fetchTeamPerformance = async () => {
      try {
        setTeamPerfLoading(true);
        const performance = await getTeamPerformance(tenantDomain, { limit: 3 });
        setTeamPerformance(performance || null);
      } catch (err) {
        console.error('Error fetching team performance:', err);
        // Don't set error - allow dashboard to render without team performance
        setTeamPerformance(null);
      } finally {
        setTeamPerfLoading(false);
      }
    };

    if (tenantDomain) {
      fetchTeamPerformance();
    }
  }, [tenantDomain]);

  // Fetch comparative analytics
  useEffect(() => {
    const fetchComparativeAnalytics = async () => {
      try {
        setComparativeLoading(true);
        const analytics = await getComparativeAnalytics(tenantDomain);
        setComparativeAnalytics(analytics || null);
      } catch (err) {
        console.error('Error fetching comparative analytics:', err);
        // Don't set error - allow dashboard to render without comparative analytics
        setComparativeAnalytics(null);
      } finally {
        setComparativeLoading(false);
      }
    };

    if (tenantDomain) {
      fetchComparativeAnalytics();
    }
  }, [tenantDomain]);

  // Fetch OKR progress analytics
  useEffect(() => {
    const fetchOKRProgress = async () => {
      try {
        setOKRProgressLoading(true);
        const progress = await getOKRProgress(tenantDomain, {
          period: 90,
          granularity: 'weekly'
        });
        setOKRProgress(progress || null);
      } catch (err) {
        console.error('Error fetching OKR progress:', err);
        // Don't set error - allow dashboard to render without OKR progress
        setOKRProgress(null);
      } finally {
        setOKRProgressLoading(false);
      }
    };

    if (tenantDomain) {
      fetchOKRProgress();
    }
  }, [tenantDomain]);

  // Fetch REAL employee data from workforce API
  useEffect(() => {
    const fetchRealEmployees = async () => {
      try {
        console.log('Fetching REAL employees from workforce API using getMyAllReports...');
        const { items } = await getMyAllReports({ limit: 100 });
        setRealEmployees(items);
        console.log(`Loaded ${items.length} real employees:`, items.map(e => e.personalInfo.name));
      } catch (err) {
        console.error('Error fetching real employees:', err);
        setRealEmployees([]);
      }
    };

    fetchRealEmployees();
  }, []);

  // Transform performance by theme data for chart display
  const performancePorTema = performanceByTheme.map(item => ({
    tema: item.theme.replace(/ /g, '\n'), // Replace spaces with newlines for multiline labels
    media: item.averageScore
  }));

  // Helper to format relative time
  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'hoje';
    if (diffInDays === 1) return '1 dia atrás';
    if (diffInDays < 7) return `${diffInDays} dias atrás`;
    if (diffInDays < 14) return '1 semana atrás';
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} semanas atrás`;
    return `${Math.floor(diffInDays / 30)} meses atrás`;
  };

  // Helper to translate feedback status
  const translateFeedbackStatus = (status: string): string => {
    const translations: Record<string, string> = {
      'delivered': 'Entregue',
      'draft': 'Rascunho',
      'follow-up-scheduled': 'Follow-up agendado'
    };
    return translations[status] || status;
  };

  // Helper to translate meeting status
  const translateMeetingStatus = (status: string): string => {
    const translations: Record<string, string> = {
      'confirmed': 'Confirmado',
      'scheduled': 'Agendado',
      'completed': 'Concluído',
      'cancelled': 'Cancelado'
    };
    return translations[status] || status;
  };

  // Helper to translate priority levels
  const translatePriority = (priority: string): string => {
    const translations: Record<string, string> = {
      'high': 'Alta prioridade',
      'medium': 'Média prioridade',
      'low': 'Baixa prioridade'
    };
    return translations[priority.toLowerCase()] || priority;
  };

  // Helper to translate severity levels
  const translateSeverity = (severity: string): string => {
    const translations: Record<string, string> = {
      'critical': 'Crítico',
      'high': 'Alto',
      'warning': 'Atenção',
      'moderate': 'Moderado',
      'low': 'Baixo'
    };
    return translations[severity.toLowerCase()] || severity;
  };

  // Helper to translate risk types
  const translateRiskType = (type: string): string => {
    const translations: Record<string, string> = {
      'low-feedback': 'Baixa atividade de feedback',
      'low-engagement': 'Baixo engajamento',
      'high-turnover-risk': 'Alto risco de rotatividade',
      'performance-decline': 'Declínio de desempenho',
      'missed-one-on-ones': 'Reuniões 1:1 perdidas',
      'goal-misalignment': 'Desalinhamento de metas'
    };
    return translations[type] || type.replace(/-/g, ' ');
  };

  // Helper to translate action categories
  const translateActionCategory = (category: string): string => {
    const translations: Record<string, string> = {
      'feedback': 'Feedback',
      'engagement': 'Engajamento',
      'development': 'Desenvolvimento',
      'communication': 'Comunicação',
      'recognition': 'Reconhecimento',
      'one-on-one': 'Reuniões 1:1',
      'goals': 'Metas',
      'performance': 'Desempenho'
    };
    return translations[category.toLowerCase()] || category;
  };

  // Helper to translate risk descriptions
  const translateRiskDescription = (description: string): string => {
    const translations: Record<string, string> = {
      'Low feedback activity detected': 'Baixa atividade de feedback detectada',
      'Climate survey satisfaction below acceptable threshold': 'Satisfação na pesquisa de clima abaixo do aceitável',
      '1-on-1 meetings below recommended frequency': 'Reuniões 1:1 abaixo da frequência recomendada',
      'Performance trend showing decline': 'Tendência de desempenho em declínio',
      'Employee engagement scores dropping': 'Pontuação de engajamento em queda'
    };
    return translations[description] || description;
  };

  // Helper to translate suggested actions
  const translateSuggestedAction = (action: string): string => {
    const translations: Record<string, string> = {
      'Encourage feedback culture through team meetings': 'Incentivar cultura de feedback através de reuniões em equipe',
      'Provide feedback training for team members': 'Providenciar treinamento de feedback para membros da equipe',
      'Set feedback frequency targets': 'Definir metas de frequência de feedback',
      'Conduct climate survey analysis': 'Realizar análise da pesquisa de clima',
      'Schedule team building activities': 'Agendar atividades de integração de equipe',
      'Address concerns raised in surveys': 'Abordar preocupações levantadas nas pesquisas',
      'Schedule regular 1-on-1 meetings': 'Agendar reuniões 1:1 regulares',
      'Review and adjust meeting frequency': 'Revisar e ajustar frequência de reuniões'
    };
    return translations[action] || action;
  };

  const getHeatmapColor = (score: number) => {
    if (score >= 85) return 'bg-success';
    if (score >= 70) return 'bg-warning';
    return 'bg-error';
  };

  const getHeatmapOpacity = (score: number) => {
    if (score >= 85) return 'opacity-100';
    if (score >= 70) return 'opacity-70';
    return 'opacity-50';
  };

  const getTipoFeedbackClass = (tipo: string) => {
    switch (tipo) {
      case 'Reconhecimento':
        return 'bg-success/20 text-success border-success';
      case 'Construtivo':
        return 'bg-warning/20 text-warning border-warning';
      case 'Crítico':
        return 'bg-error/20 text-error border-error';
      default:
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray';
    }
  };

  const getStatusClass = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'confirmado':
      case 'confirmed':
        return 'bg-success/20 text-success border-success';
      case 'pendente':
      case 'scheduled':
        return 'bg-warning/20 text-warning border-warning';
      default:
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray';
    }
  };

  const getAvaliacaoStatusClass = (status: string) => {
    switch (status) {
      case 'Excelente':
      case 'excellent':
        return 'bg-success/20 text-success border-success';
      case 'Acima da média':
      case 'above-average':
        return 'bg-brand-blue/20 text-brand-blue border-brand-blue';
      case 'Adequado':
      case 'adequate':
        return 'bg-warning/20 text-warning border-warning';
      default:
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="dashboard-grid">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="card bg-brand-dark-light border-brand-gray/10">
              <CardContent className="p-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-brand-dark rounded-monumenta w-24 mb-2"></div>
                  <div className="h-8 bg-brand-dark rounded-monumenta w-16"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center text-brand-gray py-8">
          Loading dashboard data...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card className="card bg-error/10 border-error/30">
          <CardContent className="p-6">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-error mx-auto mb-4" />
              <h3 className="text-white text-lg font-medium font-display mb-2">Error Loading Dashboard</h3>
              <p className="text-brand-gray mb-4">{error.message}</p>
              <Button
                onClick={() => window.location.reload()}
                className="btn-danger"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No data state
  if (!dashboardData) {
    return (
      <div className="space-y-6">
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-6 text-center text-brand-gray">
            No dashboard data available
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get trend icon component
  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-success" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-error" />;
    return <span className="h-4 w-4 text-brand-gray">—</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header com Indicadores Gerais - Real Data */}
      {SHOW_ALL_SECTIONS && (
      <div className="dashboard-grid">
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-brand-gray text-xs">Feedbacks Enviados</p>
                <p className="text-white text-2xl font-bold mt-1">
                  {dashboardData.feedbackStats.sent}
                </p>
                <p className="text-brand-gray text-xs mt-1">
                  {dashboardData.feedbackStats.pending} pendentes
                </p>
              </div>
              <div className="w-10 h-10 bg-brand-blue/10 rounded-monumenta flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-brand-blue" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-brand-gray text-xs">Próximos 1:1</p>
                <p className="text-white text-2xl font-bold mt-1">
                  {dashboardData.upcomingOneOnOnes.count}
                </p>
                <p className="text-brand-gray text-xs mt-1">
                  {dashboardData.upcomingOneOnOnes.next7Days} nos próximos 7 dias
                </p>
              </div>
              <div className="w-10 h-10 bg-success/10 rounded-monumenta flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-brand-gray text-xs">Média Desempenho</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-white text-2xl font-bold">
                    {dashboardData.performanceMetrics.individualScore}
                  </p>
                  {getTrendIcon(dashboardData.performanceMetrics.trend)}
                </div>
                <p className="text-brand-gray text-xs mt-1">
                  Equipe: {dashboardData.performanceMetrics.teamAverage}
                </p>
              </div>
              <div className="w-10 h-10 bg-warning/10 rounded-monumenta flex items-center justify-center">
                <Target className="h-5 w-5 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-brand-gray text-xs">Satisfação Clima</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-white text-2xl font-bold">
                    {dashboardData.climateSatisfaction.overallScore}%
                  </p>
                  {getTrendIcon(dashboardData.climateSatisfaction.trend)}
                </div>
              </div>
              <div className="w-10 h-10 bg-brand-primary/10 rounded-monumenta flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-brand-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Seção 1: FEEDBACKS */}
      <div className="grid grid-cols-1 gap-6">
        {/* 1.1 - Últimos Feedbacks Enviados */}
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 font-display">
                <MessageSquare className="h-5 w-5 text-brand-blue" />
                Últimos Feedbacks Enviados
              </CardTitle>
              <Button variant="ghost" className="btn-ghost btn-sm text-brand-blue hover:text-brand-primary">
                Ver todos
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {feedbacksLoading ? (
              <div className="text-center py-6">
                <div className="animate-pulse text-brand-gray">Carregando feedbacks...</div>
              </div>
            ) : recentFeedbacks.length === 0 ? (
              <div className="text-center py-6">
                <MessageSquare className="h-12 w-12 text-brand-gray/50 mx-auto mb-4" />
                <h3 className="text-white font-display text-lg mb-2">Nenhum feedback ainda</h3>
                <p className="text-brand-gray text-sm mb-4 max-w-md mx-auto">
                  Compartilhe reconhecimentos e sugestões construtivas com sua equipe para fortalecer a cultura de feedback.
                </p>
                <Link to="/performance/feedbacks/quick">
                  <Button className="btn-primary">
                    Enviar Feedback
                  </Button>
                </Link>
              </div>
            ) : (
              recentFeedbacks.slice(0, 3).map((feedback) => (
                <div
                  key={feedback.feedbackId}
                  className="bg-brand-dark rounded-monumenta p-4 hover:bg-brand-dark-lighter transition-colors duration-200 cursor-pointer"
                  onClick={() => handleFeedbackClick(feedback)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getTipoFeedbackClass(feedback.category)}>
                          {feedback.category}
                        </Badge>
                        <span className="text-brand-gray text-xs">{getRelativeTime(feedback.createdAt)}</span>
                      </div>
                      <p className="text-white text-sm">Para: {feedback.toEmployeeName}</p>
                      {feedback.project && <p className="text-brand-gray text-xs">Projeto: {feedback.project}</p>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-brand-gray/20">
                    <span className="text-brand-gray text-xs">{translateFeedbackStatus(feedback.status)}</span>
                    <Button
                      variant="link"
                      className="btn-ghost text-brand-blue hover:text-brand-primary p-0 h-auto text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedbackClick(feedback);
                      }}
                    >
                      Ver detalhes →
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* 1.2 - Próximos 1:1 Agendados */}
        {SHOW_ALL_SECTIONS && (
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 font-display">
                <CalendarIcon className="h-5 w-5 text-success" />
                Próximos 1:1 Agendados
              </CardTitle>
              <Button variant="ghost" className="btn-ghost btn-sm text-success hover:text-brand-primary">
                Ver agenda
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {meetingsLoading ? (
              <div className="text-center py-6">
                <div className="animate-pulse text-brand-gray">Carregando reuniões...</div>
              </div>
            ) : upcomingMeetings.length === 0 ? (
              <div className="text-center py-6">
                <CalendarIcon className="h-12 w-12 text-brand-gray/50 mx-auto mb-4" />
                <h3 className="text-white font-display text-lg mb-2">Nenhuma reunião agendada</h3>
                <p className="text-brand-gray text-sm mb-4 max-w-md mx-auto">
                  Agende 1:1s regulares para acompanhar o desenvolvimento e alinhar expectativas com seu time.
                </p>
                <Link to="/performance/feedbacks">
                  <Button className="btn-primary">
                    Agendar 1:1
                  </Button>
                </Link>
              </div>
            ) : (
              upcomingMeetings.map((reuniao) => (
                <div key={reuniao.meetingId} className="bg-brand-dark rounded-monumenta p-4 hover:bg-brand-dark-lighter transition-colors duration-200">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>{reuniao.employeeName.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white text-sm font-medium">{reuniao.employeeName}</p>
                          <p className="text-brand-gray text-xs">{reuniao.employeeRole}</p>
                        </div>
                        <Badge className={getStatusClass(translateMeetingStatus(reuniao.status))}>
                          {translateMeetingStatus(reuniao.status)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-brand-gray">
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {formatDate(reuniao.date)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {reuniao.time} ({reuniao.duration}min)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Seção 2: AVALIAÇÃO DE DESEMPENHO */}
      {SHOW_ALL_SECTIONS && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 2.1 - Gráfico Performance por Tema */}
        <Card className="card bg-brand-dark-light border-brand-gray/10 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <Target className="h-5 w-5 text-warning" />
                  Performance por Tema - Desempenho
                </CardTitle>
                <p className="text-brand-gray text-sm">Média das avaliações em cada tema de competência</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-pulse text-brand-gray">
                    Carregando dados de performance...
                  </div>
                </div>
              </div>
            ) : performancePorTema.length === 0 ? (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center px-4">
                  <Target className="h-12 w-12 text-brand-gray/50 mx-auto mb-4" />
                  <h3 className="text-white font-display text-lg mb-2">Aguardando dados de performance</h3>
                  <p className="text-brand-gray text-sm mb-4 max-w-md mx-auto">
                    Complete avaliações para visualizar análises e tendências de desempenho por tema e competência.
                  </p>
                  <Link to="/performance/avaliacoes">
                    <Button className="btn-primary">
                      Criar Avaliação
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performancePorTema} margin={{ top: 20, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="tema"
                      stroke="#9CA3AF"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis stroke="#9CA3AF" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#F3F4F6' }}
                    />
                    <Bar dataKey="media" radius={[8, 8, 0, 0]}>
                      {performancePorTema.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.media >= 4 ? '#10B981' :
                            entry.media >= 3 ? '#F59E0B' :
                            '#EF4444'
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2.2 - Resumo Avaliação Equipe */}
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 font-display">
              <Users className="h-5 w-5 text-brand-blue" />
              Desempenho da Equipe
            </CardTitle>
            <p className="text-brand-gray text-xs">Coordenadores subordinados</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {teamPerfLoading ? (
              <div className="text-center py-6">
                <div className="animate-pulse text-brand-gray">Carregando equipe...</div>
              </div>
            ) : !teamPerformance || teamPerformance.data.members.length === 0 ? (
              <div className="text-center py-6">
                <Users className="h-12 w-12 text-brand-gray/50 mx-auto mb-4" />
                <h3 className="text-white font-display text-lg mb-2">Dados da equipe indisponíveis</h3>
                <p className="text-brand-gray text-sm max-w-sm mx-auto">
                  Complete avaliações dos membros da equipe para visualizar o desempenho consolidado.
                </p>
              </div>
            ) : (
              <>
                {teamPerformance.data.members.slice(0, 3).map((membro, idx) => {
                  const strengthsCount = membro.strengthThemes.length;
                  const improvementsCount = membro.improvementThemes.length;

                  // Try to get real employee data for enhanced display
                  const realEmployee = realEmployees[idx];
                  const displayName = realEmployee?.personalInfo.name || membro.name;
                  const displayRole = realEmployee?.currentEngagement?.roleName || membro.role;
                  const avatarUrl = realEmployee?.personalInfo.pictureUrl;

                  return (
                    <div key={membro.employeeId} className="bg-brand-dark rounded-monumenta p-3 hover:bg-brand-dark-lighter transition-colors duration-200">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          {avatarUrl ? <AvatarImage src={avatarUrl} /> : null}
                          <AvatarFallback>{displayName.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <p className="text-white text-sm font-medium">{displayName}</p>
                              <p className="text-brand-gray text-xs">{displayRole}</p>
                            </div>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${
                                membro.averageScore >= 4 ? 'text-success' :
                                membro.averageScore >= 3 ? 'text-warning' :
                                'text-error'
                              }`}>
                                {membro.averageScore.toFixed(1)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={getAvaliacaoStatusClass(membro.status)} variant="outline">
                              {membro.status === 'excellent' ? 'Excelente' :
                               membro.status === 'above-average' ? 'Acima da média' :
                               membro.status === 'adequate' ? 'Adequado' : 'Atenção'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div className="flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 text-success" />
                              <span className="text-brand-gray">{strengthsCount} excelentes</span>
                            </div>
                            {improvementsCount > 0 && (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3 text-warning" />
                                <span className="text-brand-gray">{improvementsCount} atenção</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button variant="outline" className="btn-secondary btn-sm w-full">
                  Ver todas as avaliações
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Seção 3: AVALIAÇÃO INDIVIDUAL */}
      {SHOW_ALL_SECTIONS && individualProgress && individualProgress.data.activeOKRs.length > 0 && (
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <Briefcase className="h-5 w-5 text-brand-primary" />
                  Resumo Avaliação Individual
                </CardTitle>
                <p className="text-brand-gray text-sm">Metas e KRs ativos</p>
              </div>
              <Button variant="ghost" className="btn-ghost btn-sm text-brand-primary hover:text-brand-primary">
                Ver detalhes
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="card bg-brand-dark border-brand-gray/10">
                <CardContent className="p-4 text-center">
                  <p className="text-brand-gray text-xs mb-1">OKRs Ativos</p>
                  <p className="text-white text-3xl font-bold font-display">{individualProgress.data.activeOKRs.length}</p>
                </CardContent>
              </Card>
              <Card className="card bg-brand-dark border-brand-gray/10">
                <CardContent className="p-4 text-center">
                  <p className="text-brand-gray text-xs mb-1">Avaliações</p>
                  <p className="text-white text-3xl font-bold font-display">{individualProgress.data.recentEvaluations.length}</p>
                </CardContent>
              </Card>
              <Card className="card bg-brand-dark border-brand-gray/10">
                <CardContent className="p-4 text-center">
                  <p className="text-brand-gray text-xs mb-1">Feedbacks Enviados</p>
                  <p className="text-white text-3xl font-bold font-display">{individualProgress.data.feedbackStats.sent}</p>
                </CardContent>
              </Card>
              <Card className="card bg-brand-dark border-brand-gray/10">
                <CardContent className="p-4 text-center">
                  <p className="text-brand-gray text-xs mb-1">Reuniões 1:1</p>
                  <p className="text-success text-3xl font-bold font-display">{individualProgress.data.upcomingMeetings.length}</p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium font-display">OKRs Ativos</h4>
              </div>
              {individualProgress.data.activeOKRs.map((okr, index) => (
                <div key={okr.okrId} className="bg-brand-dark rounded-monumenta p-4 hover:bg-brand-dark-lighter transition-colors duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white text-sm font-medium">{okr.title}</p>
                      <p className="text-brand-gray text-xs">
                        Status: {okr.status === 'active' ? 'Ativo' : okr.status === 'completed' ? 'Concluído' : 'Em progresso'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-success font-bold">{okr.progress}%</p>
                      <p className="text-brand-gray text-xs">{okr.dueDate ? formatDate(okr.dueDate) : '-'}</p>
                    </div>
                  </div>
                  <Progress value={okr.progress} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seção 4: COMPARATIVE ANALYTICS - BENCHMARKING */}
      {SHOW_ALL_SECTIONS && comparativeAnalytics && (
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <Target className="h-5 w-5 text-brand-primary" />
                  Análise Comparativa de Performance
                </CardTitle>
                <p className="text-brand-gray text-sm">
                  Sua performance vs. média da equipe e empresa
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {comparativeLoading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="animate-pulse text-brand-gray">Carregando análise comparativa...</div>
              </div>
            ) : (
              <>
                {/* Comparative Bar Chart */}
                <div className="h-80 mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        {
                          metric: 'Avaliação',
                          'Você': comparativeAnalytics.individual.evaluationScore,
                          'Equipe': comparativeAnalytics.teamAverage.evaluationScore,
                          'Empresa': comparativeAnalytics.companyAverage.evaluationScore
                        },
                        {
                          metric: 'Feedbacks',
                          'Você': comparativeAnalytics.individual.feedbackCount,
                          'Equipe': comparativeAnalytics.teamAverage.feedbackCount,
                          'Empresa': null
                        },
                        {
                          metric: 'OKR %',
                          'Você': comparativeAnalytics.individual.okrCompletionRate,
                          'Equipe': comparativeAnalytics.teamAverage.okrCompletionRate,
                          'Empresa': null
                        }
                      ]}
                      margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="metric" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F3F4F6' }}
                      />
                      <Bar dataKey="Você" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="Equipe" fill="#10B981" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="Empresa" fill="#F59E0B" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Insights Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {comparativeAnalytics.insights.map((insight, idx) => (
                    <Card
                      key={idx}
                      className={`card border-l-4 ${
                        insight.severity === 'positive' ? 'bg-success/10 border-success' :
                        insight.severity === 'negative' ? 'bg-error/10 border-error' :
                        'bg-brand-dark border-brand-gray'
                      }`}
                    >
                      <CardContent className="p-4">
                        <p className="text-brand-gray text-xs font-medium mb-1">{insight.metric}</p>
                        <p className={`text-sm ${
                          insight.severity === 'positive' ? 'text-success' :
                          insight.severity === 'negative' ? 'text-error' :
                          'text-white'
                        }`}>
                          {insight.message}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção 5: OKR PROGRESS ANALYTICS - TREND TRACKING */}
      {SHOW_ALL_SECTIONS && okrProgress && okrProgress.okrs.length > 0 && (
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <TrendingUp className="h-5 w-5 text-success" />
                  Progresso de OKRs - Análise de Tendências
                </CardTitle>
                <p className="text-brand-gray text-sm">
                  {okrProgress.summary.totalOKRs} OKRs ativos | {okrProgress.summary.onTrackCount} no caminho certo
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {okrProgressLoading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="animate-pulse text-brand-gray">Carregando progresso de OKRs...</div>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <Card className="card bg-brand-dark border-brand-gray/10">
                    <CardContent className="p-4 text-center">
                      <p className="text-brand-gray text-xs mb-1">Progresso Médio</p>
                      <p className="text-white text-3xl font-bold font-display">{okrProgress.summary.averageProgress}%</p>
                    </CardContent>
                  </Card>
                  <Card className="card bg-brand-dark border-brand-gray/10">
                    <CardContent className="p-4 text-center">
                      <p className="text-brand-gray text-xs mb-1">No Caminho</p>
                      <p className="text-success text-3xl font-bold font-display">{okrProgress.summary.onTrackCount}</p>
                    </CardContent>
                  </Card>
                  <Card className="card bg-brand-dark border-brand-gray/10">
                    <CardContent className="p-4 text-center">
                      <p className="text-brand-gray text-xs mb-1">Em Risco</p>
                      <p className="text-warning text-3xl font-bold font-display">{okrProgress.summary.atRiskCount}</p>
                    </CardContent>
                  </Card>
                  <Card className="card bg-brand-dark border-brand-gray/10">
                    <CardContent className="p-4 text-center">
                      <p className="text-brand-gray text-xs mb-1">Atrasados</p>
                      <p className="text-error text-3xl font-bold font-display">{okrProgress.summary.behindCount}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* OKR Progress Charts */}
                {okrProgress.okrs.slice(0, 2).map((okrData) => (
                  <div key={okrData.okr.okrId} className="mb-8 last:mb-0">
                    <div className="mb-4">
                      <h4 className="text-white font-medium font-display mb-1">{okrData.okr.title}</h4>
                      <p className="text-brand-gray text-sm">
                        Progresso geral: {okrData.overallProgress}% | {okrData.keyResults.length} Key Results
                      </p>
                    </div>

                    {/* Line Chart for Key Results Progress */}
                    {okrData.keyResults[0]?.trend && okrData.keyResults[0].trend.length > 0 && (
                      <div className="h-64 mb-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={okrData.keyResults[0].trend}
                            margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                              dataKey="date"
                              stroke="#9CA3AF"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(value) => {
                                const date = new Date(value);
                                return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                              }}
                            />
                            <YAxis stroke="#9CA3AF" domain={[0, 100]} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                              labelStyle={{ color: '#F3F4F6' }}
                            />
                            <Line
                              type="monotone"
                              dataKey="completionRate"
                              stroke="#3B82F6"
                              strokeWidth={2}
                              dot={{ fill: '#3B82F6', r: 4 }}
                              name="Progresso %"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Key Results List */}
                    <div className="space-y-3">
                      {okrData.keyResults.map((kr) => (
                        <div key={kr.kr.krId} className="bg-brand-dark rounded-monumenta p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-white text-sm">{kr.kr.title}</p>
                            <Badge className={`${
                              kr.status === 'on-track' ? 'bg-success/20 text-success border-success' :
                              kr.status === 'at-risk' ? 'bg-warning/20 text-warning border-warning' :
                              kr.status === 'behind' ? 'bg-error/20 text-error border-error' :
                              'bg-brand-blue/20 text-brand-blue border-brand-blue'
                            }`}>
                              {kr.status === 'on-track' ? 'No caminho' :
                               kr.status === 'at-risk' ? 'Em risco' :
                               kr.status === 'behind' ? 'Atrasado' : 'Concluído'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-brand-gray">
                            <span>{kr.currentValue} / {kr.targetValue}</span>
                            <span>•</span>
                            <span>{kr.completionRate}% concluído</span>
                            {kr.completionPrediction.predictedDate && (
                              <>
                                <span>•</span>
                                <span className={kr.completionPrediction.onTrack ? 'text-success' : 'text-error'}>
                                  {kr.completionPrediction.onTrack ? 'No prazo' : 'Atrasado'}
                                </span>
                              </>
                            )}
                          </div>
                          <Progress value={kr.completionRate} className="h-2 mt-2" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção 6: MAPA DE CALOR - CLIMA ORGANIZACIONAL */}
      {SHOW_ALL_SECTIONS && climateTrends && climateTrends.heatmapData.length > 0 && (
        <Card className="card bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <TrendingUp className="h-5 w-5 text-brand-primary" />
                  Mapa de Calor - Satisfação por Tema
                </CardTitle>
                <p className="text-brand-gray text-sm">
                  Pesquisa de Clima Organizacional - {climateTrends.metadata.totalSurveys} pesquisas
                </p>
              </div>
              <Button variant="ghost" className="btn-ghost btn-sm text-brand-primary hover:text-brand-primary">
                Ver pesquisa completa
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {climateTrends.heatmapData.map((item, index) => {
                // Convert 1-5 scale to 0-100 scale
                const scorePercent = Math.round((item.score / 5) * 100);
                return (
                  <div
                    key={index}
                    className={`rounded-monumenta p-4 transition-all hover:scale-105 cursor-pointer ${getHeatmapColor(scorePercent)} ${getHeatmapOpacity(scorePercent)}`}
                  >
                    <div className="text-center">
                      <p className="text-white font-medium text-sm mb-2 font-display">{item.category}</p>
                      <p className="text-white text-2xl font-bold font-display">{scorePercent}</p>
                      <p className="text-white text-xs opacity-80 mt-1">de 100</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-error rounded"></div>
                <span className="text-brand-gray text-sm">Crítico (&lt; 70)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-warning rounded"></div>
                <span className="text-brand-gray text-sm">Atenção (70-84)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-success rounded"></div>
                <span className="text-brand-gray text-sm">Excelente (≥ 85)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seção 7: TEAM INSIGHTS - PADRÕES E RECOMENDAÇÕES */}
      {SHOW_ALL_SECTIONS && teamInsights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 5.1 - Engagement Patterns */}
          <Card className="card bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 font-display">
                <Users className="h-5 w-5 text-brand-blue" />
                Padrões de Engajamento da Equipe
              </CardTitle>
              <p className="text-brand-gray text-sm">Últimos {teamInsights.metadata.periodCovered.startDate ? '90 dias' : 'período'}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {insightsLoading ? (
                <div className="text-center py-6">
                  <div className="animate-pulse text-brand-gray">Carregando insights...</div>
                </div>
              ) : teamInsights.engagementPatterns.length === 0 ? (
                <div className="text-center py-6 text-brand-gray">
                  <Users className="h-12 w-12 text-brand-gray/50 mx-auto mb-2" />
                  <p>Nenhum padrão detectado ainda</p>
                </div>
              ) : (
                teamInsights.engagementPatterns.map((pattern, idx) => (
                  <div key={idx} className={`bg-brand-dark rounded-monumenta p-4 border-l-4 hover:bg-brand-dark-lighter transition-colors duration-200 ${
                    pattern.trend === 'improving' ? 'border-success' :
                    pattern.trend === 'declining' ? 'border-error' :
                    'border-brand-gray'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium text-sm">{pattern.metric}</span>
                          <Badge className={`${
                            pattern.significance === 'high' ? 'bg-error/20 text-error border-error' :
                            pattern.significance === 'medium' ? 'bg-warning/20 text-warning border-warning' :
                            'bg-brand-gray/20 text-brand-gray border-brand-gray'
                          }`}>
                            {translateSeverity(pattern.significance)}
                          </Badge>
                        </div>
                        <p className="text-brand-gray text-xs">{pattern.insight}</p>
                      </div>
                      <div className="ml-2">
                        {pattern.trend === 'improving' ? (
                          <TrendingUp className="h-5 w-5 text-success" />
                        ) : pattern.trend === 'declining' ? (
                          <TrendingDown className="h-5 w-5 text-error" />
                        ) : (
                          <span className="text-brand-gray">—</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-xs">
                      <span className="text-brand-blue font-medium">{pattern.currentValue}</span>
                      <span className="text-brand-gray ml-1">valor atual</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* 5.2 - Action Items & Recommendations */}
          <Card className="card bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 font-display">
                <Target className="h-5 w-5 text-warning" />
                Itens de Ação Recomendados
              </CardTitle>
              <p className="text-brand-gray text-sm">Baseado em padrões detectados</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {insightsLoading ? (
                <div className="text-center py-6">
                  <div className="animate-pulse text-brand-gray">Carregando recomendações...</div>
                </div>
              ) : teamInsights.actionItems.length === 0 ? (
                <div className="text-center py-6 text-brand-gray">
                  <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-2" />
                  <p>Tudo certo! Nenhuma ação necessária</p>
                </div>
              ) : (
                teamInsights.actionItems.map((item, idx) => (
                  <div key={idx} className={`bg-brand-dark rounded-monumenta p-4 border-l-4 hover:bg-brand-dark-lighter transition-colors duration-200 ${
                    item.priority === 'high' ? 'border-error' :
                    item.priority === 'medium' ? 'border-warning' :
                    'border-brand-blue'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium text-sm">{translateActionCategory(item.category)}</span>
                          <Badge className={`${
                            item.priority === 'high' ? 'bg-error/20 text-error border-error' :
                            item.priority === 'medium' ? 'bg-warning/20 text-warning border-warning' :
                            'bg-brand-blue/20 text-brand-blue border-brand-blue'
                          }`}>
                            {translatePriority(item.priority)}
                          </Badge>
                        </div>
                        <p className="text-brand-gray text-xs mb-2">{translateRiskDescription(item.description)}</p>
                        <p className="text-brand-gray text-xs italic">Impacto: {item.impact}</p>
                      </div>
                    </div>
                    {item.suggestedActions && item.suggestedActions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-brand-gray/20">
                        <p className="text-brand-gray text-xs font-medium mb-1">Ações sugeridas:</p>
                        <ul className="list-disc list-inside text-brand-gray text-xs space-y-1">
                          {item.suggestedActions.map((action, actionIdx) => (
                            <li key={actionIdx}>{translateSuggestedAction(action)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* 5.3 - Risk Factors */}
          {teamInsights.riskFactors && teamInsights.riskFactors.length > 0 && (
            <Card className="card bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <AlertTriangle className="h-5 w-5 text-error" />
                  Fatores de Risco Identificados
                </CardTitle>
                <p className="text-brand-gray text-sm">Requer atenção imediata</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {teamInsights.riskFactors.map((risk, idx) => (
                  <div key={idx} className={`bg-brand-dark rounded-monumenta p-4 border-l-4 hover:bg-brand-dark-lighter transition-colors duration-200 ${
                    risk.severity === 'critical' ? 'border-error' :
                    risk.severity === 'warning' ? 'border-warning' :
                    'border-warning'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className={`h-4 w-4 ${
                            risk.severity === 'critical' ? 'text-error' :
                            risk.severity === 'warning' ? 'text-warning' :
                            'text-warning'
                          }`} />
                          <Badge className={`${
                            risk.severity === 'critical' ? 'bg-error/20 text-error border-error' :
                            risk.severity === 'warning' ? 'bg-warning/20 text-warning border-warning' :
                            'bg-warning/20 text-warning border-warning'
                          }`}>
                            {translateSeverity(risk.severity)}
                          </Badge>
                        </div>
                        <p className="text-white font-medium text-sm mb-1">{translateRiskType(risk.type)}</p>
                        <p className="text-brand-gray text-xs">{translateRiskDescription(risk.description)}</p>
                      </div>
                    </div>
                    <div className="mt-2 text-xs">
                      <span className="text-error font-medium">{risk.affectedEmployees}</span>
                      <span className="text-brand-gray ml-1">colaboradores afetados</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* 5.4 - Opportunities */}
          {teamInsights.opportunities && teamInsights.opportunities.length > 0 && (
            <Card className="card bg-brand-dark-light border-brand-gray/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 font-display">
                  <TrendingUp className="h-5 w-5 text-success" />
                  Oportunidades Identificadas
                </CardTitle>
                <p className="text-brand-gray text-sm">Pontos fortes para alavancar</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {teamInsights.opportunities.map((opp, idx) => (
                  <div key={idx} className="bg-brand-dark rounded-monumenta p-4 border-l-4 border-success hover:bg-brand-dark-lighter transition-colors duration-200">
                    <div className="flex items-start gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm mb-1">{translateRiskType(opp.type)}</p>
                        <p className="text-brand-gray text-xs mb-2">{translateRiskDescription(opp.description)}</p>
                        <div className="mt-2 pt-2 border-t border-brand-gray/20">
                          <p className="text-success text-xs font-medium">Recomendação:</p>
                          <p className="text-brand-gray text-xs">{translateSuggestedAction(opp.recommendation)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Feedback Detail Modal */}
      <Dialog open={isFeedbackModalOpen} onOpenChange={setIsFeedbackModalOpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedFeedback && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl text-white flex items-center gap-3">
                  {selectedFeedback.category === 'Crítico' ? (
                    <AlertTriangle className="h-6 w-6 text-error" />
                  ) : selectedFeedback.category === 'Construtivo' ? (
                    <Lightbulb className="h-6 w-6 text-warning" />
                  ) : (
                    <Star className="h-6 w-6 text-success" />
                  )}
                  Feedback {selectedFeedback.category}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-monumenta ${
                    selectedFeedback.category === 'Crítico' ? 'bg-error/10 border border-error/30' :
                    selectedFeedback.category === 'Construtivo' ? 'bg-warning/10 border border-warning/30' :
                    'bg-success/10 border border-success/30'
                  }`}>
                    <p className="text-brand-gray text-sm mb-1">Para:</p>
                    <p className="text-white font-medium">{selectedFeedback.toEmployeeName}</p>
                  </div>
                  <div className={`p-4 rounded-monumenta ${
                    selectedFeedback.category === 'Crítico' ? 'bg-error/10 border border-error/30' :
                    selectedFeedback.category === 'Construtivo' ? 'bg-warning/10 border border-warning/30' :
                    'bg-success/10 border border-success/30'
                  }`}>
                    <p className="text-brand-gray text-sm mb-1">Data:</p>
                    <p className="text-white font-medium">
                      {new Date(selectedFeedback.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Project */}
                {selectedFeedback.project && (
                  <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                    <p className="text-brand-gray text-sm mb-1">Projeto/Cliente:</p>
                    <p className="text-white">{selectedFeedback.project}</p>
                  </div>
                )}

                {/* COIN/SBI Sections */}
                <div className="space-y-4">
                  {/* Context */}
                  {selectedFeedback.context && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-brand-blue" />
                        <p className="text-brand-blue text-sm font-medium">CONTEXTO</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.context}</p>
                    </div>
                  )}

                  {/* Observation */}
                  {selectedFeedback.observation && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-brand-primary" />
                        <p className="text-brand-primary text-sm font-medium">OBSERVAÇÃO</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.observation}</p>
                    </div>
                  )}

                  {/* Impact */}
                  {selectedFeedback.impact && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-warning" />
                        <p className="text-warning text-sm font-medium">IMPACTO</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.impact}</p>
                    </div>
                  )}

                  {/* Next Steps */}
                  {selectedFeedback.nextSteps && (
                    <div className="p-4 rounded-monumenta bg-brand-dark border border-brand-gray/20">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowRight className="w-4 h-4 text-success" />
                        <p className="text-success text-sm font-medium">PRÓXIMOS PASSOS</p>
                      </div>
                      <p className="text-white leading-relaxed">{selectedFeedback.nextSteps}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-brand-gray/20">
                <Button
                  variant="outline"
                  onClick={() => setIsFeedbackModalOpen(false)}
                  className="btn-secondary"
                >
                  Fechar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResumoGeral;
